import React, { useState, useEffect } from 'react';
import { APIKeyStats as APIKeyStatsType } from '../../types/apiKeys';
import { apiKeyService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

export const APIKeyStats: React.FC = () => {
  const { showToast } = useToast();
  const [stats, setStats] = useState<APIKeyStatsType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await apiKeyService.getAPIKeyStats();
      setStats(response);
    } catch (error) {
      showToast('Failed to load statistics', 'error');
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No statistics available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">API Key Statistics</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 font-semibold">🔑</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Keys</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_keys}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <span className="text-green-600 dark:text-green-400 font-semibold">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Keys</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.active_keys}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 dark:text-yellow-400 font-semibold">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Requests Today</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_requests_today.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <span className="text-purple-600 dark:text-purple-400 font-semibold">⚡</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Response Time</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.average_response_time}s</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Endpoints */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Endpoints</h3>
          <div className="space-y-3">
            {stats.top_endpoints.map((endpoint, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{endpoint.endpoint}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{endpoint.count} requests</p>
                </div>
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${(endpoint.count / Math.max(...stats.top_endpoints.map(e => e.count))) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Usage</h3>
          <div className="space-y-3">
            {stats.recent_usage.map((usage, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{usage.endpoint}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {usage.method} • {new Date(usage.created_at).toLocaleString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  usage.response_status === 200 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800' 
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800'
                }`}>
                  {usage.response_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Status Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Status Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active_keys}</p>
            <p className="text-sm text-green-700 dark:text-green-300">Active</p>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.revoked_keys}</p>
            <p className="text-sm text-red-700 dark:text-red-300">Revoked</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.expired_keys}</p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">Expired</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{stats.total_requests_month.toLocaleString()}</p>
            <p className="text-sm text-gray-700 dark:text-gray-200">Monthly Requests</p>
          </div>
        </div>
      </div>
    </div>
  );
};
